import React, { ReactNode, useRef, useEffect, useState } from "react";
import Swiper from "swiper";
import { Swiper as OriginSwiper, SwiperSlide } from "swiper/react";

// 2025-03-26 famous autoplay 기능 수동 구현
type Props<T> = {
  slidesPerView?: number;
  list: T[];
  renderer?: (item: T) => ReactNode;
  onSlideChange?: (swiper: any) => void;
  slidesOffsetBefore?: number;
  autoplay?: boolean;
};

export default function TestSwiper<T>({
  slidesPerView,
  renderer,
  list,
  onSlideChange,
  slidesOffsetBefore = 16,
  autoplay = false,
}: Props<T>) {
  // Swiper 인스턴스 참조
  const swiperRef = useRef<Swiper | null>(null);
  // 현재 활성화된 슬라이드 인덱스
  const [activeIndex, setActiveIndex] = useState(0);
  // 타이머 관리
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // autoplay 기능 직접 구현
  useEffect(() => {
    // autoplay가 활성화된 경우에만 타이머 설정
    if (autoplay && swiperRef.current) {
      const moveToNextSlide = () => {
        if (swiperRef.current) {
          const nextIndex = (activeIndex + 1) % list.length;
          
          // 마지막 슬라이드에서 첫 번째 슬라이드로 이동할 때 
          if (activeIndex === list.length - 1 && nextIndex === 0) {
            // 첫 번째 슬라이드로 즉시 이동
            swiperRef.current.slideTo(0, 300);
            setActiveIndex(0);
            
            // 유저 정의 이벤트 호출
            if (onSlideChange) {
              onSlideChange({
                ...swiperRef.current,
                isLastIndex: false
              });
            }
            
            // 첫 번째 슬라이드에서 2.5초 더 대기
            if (timerRef.current) {
              clearTimeout(timerRef.current);
            }
            
            timerRef.current = setTimeout(() => {
              if (swiperRef.current) {
                swiperRef.current.slideTo(1, 300);
                setActiveIndex(1);
              }
            }, 2500);
            
            return;
          }
          
          // 일반적인 슬라이드 전환
          swiperRef.current.slideTo(nextIndex, 300);
          setActiveIndex(nextIndex);
        }
      };
      
      // 2.5초마다 다음 슬라이드로 이동
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      
      timerRef.current = setTimeout(moveToNextSlide, 2500);
      
      // 컴포넌트 언마운트 시 타이머 정리
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }
  }, [autoplay, activeIndex, list.length, onSlideChange]);
  
  // Swiper 초기화 핸들러
  const handleSwiper = (swiper: Swiper) => {
    swiperRef.current = swiper;
  };

  const onChange = (swiper: any) => {
    const currentIndex = swiper.realIndex;
    setActiveIndex(currentIndex);
    
    // 유저 정의 이벤트 호출 (자동 전환이 아닌 경우에만)
    const isLastIndex = currentIndex === list.length - 1;
    
    if (onSlideChange) {
      onSlideChange({
        ...swiper,
        isLastIndex
      });
    }
  };

  return (
    <div className="w-full">
      <OriginSwiper
        spaceBetween={10}
        slidesOffsetAfter={100}
        slidesOffsetBefore={slidesOffsetBefore}
        slidesPerView={slidesPerView ?? 2.5}
        onSlideChange={onChange}
        loop={false} // 루프 비활성화
        onSwiper={handleSwiper}
      >
        {list?.map((service, index) => (
          <SwiperSlide key={index}>{renderer && renderer(service)}</SwiperSlide>
        ))}
      </OriginSwiper>
    </div>
  );
}
